import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/marketing/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/marketing/components/ui/dialog';
import { useAuthStore } from '@/marketing/stores/useAuthStore';
import { extractErrorMessage } from '@/marketing/lib/errorMessage';
import { campaignScoutingAPIClient } from '@/marketing/infrastructure/influencer/campaign/scouting/campaignScoutingAPIClient';
import { patchScoutingRow, refreshActiveScoutingPage, markRelatedScoutingDataStale } from './scoutingCache';
import type { ScoutingViewRow } from './scoutingView';

export function ScoutingNotesDialog({
  campaignId,
  row,
  onClose,
}: {
  campaignId: string;
  row: ScoutingViewRow;
  onClose: () => void;
}) {
  const [text, setText] = useState(row.notes?.text ?? '');
  const [error, setError] = useState<string>();
  const lock = useRef(false);
  const cancel = useRef<HTMLButtonElement>(null);
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const actor = useAuthStore.getState().user?.email?.trim();
      if (!actor) {
        throw new Error('Sign in again before changing internal notes.');
      }
      if (!row.notes) {
        throw new Error('Internal notes are unavailable. Refresh before editing.');
      }
      return campaignScoutingAPIClient.setNotes(campaignId, row.influencerId, {
        text: text.trim(),
        expectedRevision: row.notes.revision,
        transitionBy: actor,
      });
    },
    onSuccess: (response) => {
      patchScoutingRow(client, campaignId, row.influencerId, (current) => ({ ...current, notes: response.notes }));
      markRelatedScoutingDataStale(client);
      onClose();
    },
    onError: async (failure: unknown) => {
      setError(extractErrorMessage(failure, 'Could not save notes.'));
      await refreshActiveScoutingPage(client, campaignId);
    },
    onSettled: () => {
      lock.current = false;
    },
  });
  const close = () => {
    if (!lock.current) {
      onClose();
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          close();
        }
      }}
    >
      <DialogContent
        className="ss-marketing"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          cancel.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit internal notes</DialogTitle>
          <DialogDescription>
            Notes for {row.creator?.handle ? `@${row.creator.handle}` : row.influencerId}. Only administrators can see
            these campaign notes.
          </DialogDescription>
        </DialogHeader>
        <label className="tw-space-y-1 tw-text-sm">
          <span>Notes (internal only)</span>
          <textarea
            className="tw-min-h-32 tw-w-full tw-rounded-md tw-border tw-p-2"
            value={text}
            maxLength={2000}
            disabled={mutation.isPending}
            onChange={(event) => setText(event.target.value)}
          />
        </label>
        <p className="tw-text-xs tw-text-muted-foreground">{text.length}/2000 · Leave blank to clear.</p>
        {error && (
          <div role="alert" className="tw-text-sm tw-text-red-700">
            <p>{error}</p>
            <p>Your draft is preserved. Close and reopen to review any newer note before saving again.</p>
          </div>
        )}
        <DialogFooter>
          <Button ref={cancel} variant="outline" disabled={mutation.isPending} onClick={close}>
            Cancel
          </Button>
          <Button
            disabled={mutation.isPending || !row.notes}
            onClick={() => {
              if (lock.current) {
                return;
              }
              lock.current = true;
              setError(undefined);
              mutation.mutate();
            }}
          >
            {mutation.isPending ? 'Saving notes…' : 'Save notes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
